import { Fragment } from 'react';
import { useLingui } from '@lingui/react';
import { Check, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { CARE_ALERT_CATEGORIES, getStateLabel, isCareAlertCategory } from '@/types/workflow';

// Step sequences per workflow category
const STEP_SEQUENCES: Record<string, readonly string[]> = {
  accident_notification: ['open', 'acknowledged', 'parents_notified', 'closed'],
  leave_request: ['open', 'acknowledged', 'closed'],
  timesheet_approval: ['open', 'acknowledged', 'closed'],
  child_data_update: ['open', 'acknowledged', 'closed'],
  contract_termination: ['requested', 'approved', 'completed'],
  pickup_exception: ['pending', 'acknowledged'],
  split_alert: ['pending', 'acknowledged'],
  // #2003: a rejection replaces the last step (see below).
  subsidy_report: ['to_file', 'filed', 'approved'],
  subsidy_expiry: ['pending', 'acknowledged'],
  // #2028: a skip replaces the last step (see below).
  late_extras: ['pending', 'regenerated'],
  // A dismissal replaces the last step (see below).
  coverage_breach: ['pending', 'acknowledged'],
  // The system closes it by itself (resolved); a dismissal replaces the last step too.
  // Care alerts (CARE_ALERT_CATEGORIES) follow the same sequence.
  ...Object.fromEntries(CARE_ALERT_CATEGORIES.map((c) => [c, ['pending', 'acknowledged']])),
};
const DEFAULT_STEPS = ['open', 'acknowledged', 'closed'] as const;

interface WorkflowProgressStepperProps {
  currentState?: string;
  category?: string;
}

export function WorkflowProgressStepper({ currentState, category }: WorkflowProgressStepperProps) {
  const { i18n } = useLingui();

  const isEscalated = currentState === 'escalated';
  // A revoked termination (#1678) ran the whole path and then left it through
  // its one exit, so `revoked` is appended as the final, terminal step.
  const baseSteps = STEP_SEQUENCES[category ?? ''] ?? DEFAULT_STEPS;
  const steps =
    category === 'contract_termination' && currentState === 'revoked'
      ? [...baseSteps, 'revoked']
      : category === 'subsidy_report' && currentState === 'rejected'
        ? ['to_file', 'filed', 'rejected']
        : category === 'late_extras' && currentState === 'skipped'
          ? ['pending', 'skipped']
          : category === 'coverage_breach' && currentState === 'dismissed'
            ? ['pending', 'dismissed']
            : isCareAlertCategory(category) &&
                currentState === 'dismissed'
              ? ['pending', 'dismissed']
              : isCareAlertCategory(category) &&
                  currentState === 'resolved'
                ? ['pending', 'resolved']
                : baseSteps;

  // Find current position in the step sequence
  const currentIdx = steps.indexOf(currentState ?? '');
  // If escalated or unknown, we don't highlight any step as "current" on the happy path
  const effectiveIdx = currentIdx >= 0 ? currentIdx : -1;

  return (
    <div data-testid="workflow-stepper">
      {/* Main stepper */}
      <div className="flex items-center">
        {steps.map((state, idx) => {
          // A terminal step is done, not "in progress": render it as past.
          const isFinal = state === 'revoked' && effectiveIdx === idx;
          const isPast = effectiveIdx > idx || isFinal;
          const isCurrent = effectiveIdx === idx && !isEscalated && !isFinal;
          const isFuture = effectiveIdx < idx || effectiveIdx === -1;

          return (
            <Fragment key={state}>
              <div className="flex flex-col items-center flex-shrink-0">
                <div
                  className={cn(
                    'h-8 w-8 rounded-full border-2 flex items-center justify-center text-xs font-medium transition-colors',
                    isPast && 'bg-success-muted border-success-on-surface text-success-on-surface',
                    isCurrent && 'bg-primary/10 border-primary text-primary',
                    isFuture && !isEscalated && 'border-muted-foreground/30 text-muted-foreground',
                    isFuture && isEscalated && 'border-muted-foreground/20 text-muted-foreground/50',
                  )}
                >
                  {isPast ? <Check className="h-4 w-4" aria-hidden="true" /> : idx + 1}
                </div>
                <span
                  className={cn(
                    'text-xs mt-1 text-center max-w-20 leading-tight',
                    isPast && 'text-success-on-surface font-medium',
                    isCurrent && 'text-primary font-semibold',
                    isFuture && 'text-muted-foreground',
                  )}
                >
                  {getStateLabel(i18n, state)}
                </span>
              </div>
              {idx < steps.length - 1 && (
                <div
                  className={cn(
                    'h-px flex-1 mx-2 -mt-4',
                    isPast ? 'bg-success-on-surface' : 'bg-muted-foreground/20',
                  )}
                />
              )}
            </Fragment>
          );
        })}
      </div>

      {/* Escalation indicator */}
      {isEscalated && (
        <div className="flex items-center gap-2 mt-3 p-2 rounded-md bg-destructive-muted border border-destructive-on-surface/30">
          <AlertTriangle className="h-4 w-4 text-destructive-on-surface flex-shrink-0" aria-hidden="true" />
          <span className="text-xs text-destructive-on-surface font-medium">
            {i18n._({ id: 'workflow.stepper.escalated', message: 'This item has been escalated and is off the standard path' })}
          </span>
        </div>
      )}
    </div>
  );
}
